/**
 * Product Utility Functions
 * Shared utilities for product lookups and image orientation
 */

/**
 * Find product configuration by SKU or size
 * Searches through printSizes using SKU first, then falls back to size property
 * 
 * @param {object} item - Cart item with sku and/or size properties
 * @param {object} printSizes - Product configurations keyed by SKU
 * @returns {object} { config: ProductConfig | null, key: string }
 */
export const findProductConfig = (item, printSizes) => {
  if (!printSizes || Object.keys(printSizes).length === 0) {
    return { config: null, key: item.sku || item.size || 'unknown' };
  }

  // First try direct SKU lookup (primary key)
  if (item.sku && printSizes[item.sku]) {
    return { config: printSizes[item.sku], key: item.sku };
  }

  // Then try to find by size property (backward compatibility)
  if (item.size) {
    const foundEntry = Object.entries(printSizes).find(([key, product]) => 
      product.size === item.size
    );
    if (foundEntry) {
      return { config: foundEntry[1], key: foundEntry[0] };
    }
  }

  return { config: null, key: item.sku || item.size || 'unknown' };
};

/**
 * Get aspect ratio string from product config
 * 
 * @param {object} config - Product configuration with crop_ratio_x and crop_ratio_y
 * @returns {string|null} Ratio string like "6:4" or null if not available
 */
export const getProductRatio = (config) => {
  if (!config || !config.crop_ratio_x || !config.crop_ratio_y) {
    return null;
  }
  return `${config.crop_ratio_x}:${config.crop_ratio_y}`;
};

/**
 * Determine default orientation based on image dimensions
 * 
 * @param {string} imageData - Base64 image data (with or without data:image prefix)
 * @returns {Promise<'portrait'|'landscape'>} The natural orientation of the image
 */
export const getImageOrientation = (imageData) => {
  return new Promise((resolve) => {
    if (!imageData) {
      console.warn('getImageOrientation: No image data provided, defaulting to landscape');
      resolve('landscape');
      return;
    }

    // Ensure imageSrc has data:image prefix
    const fullImageSrc = imageData.startsWith('data:') 
      ? imageData 
      : `data:image/jpeg;base64,${imageData}`;

    const img = new Image();
    
    img.onload = () => {
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      
      // Portrait: height > width, Landscape: width >= height
      const orientation = height > width ? 'portrait' : 'landscape';
      
      console.log(`📐 Image orientation: ${width}x${height} → ${orientation}`);
      resolve(orientation);
    };

    img.onerror = () => {
      console.warn('getImageOrientation: Failed to load image, defaulting to landscape');
      resolve('landscape');
    };

    img.src = fullImageSrc;
  });
};

/**
 * Get orientation for multiple images
 * 
 * @param {Array} images - Array of image objects with image_data or preview properties
 * @returns {Promise<Map<string, 'portrait'|'landscape'>>} Map of filename to orientation
 */
export const getImagesOrientations = async (images) => {
  const orientations = new Map();
  
  await Promise.all(
    images.map(async (image) => {
      const imageData = image.image_data || image.preview;
      if (imageData) {
        const orientation = await getImageOrientation(imageData);
        orientations.set(image.filename, orientation);
      } else {
        // Default to landscape if no image data
        orientations.set(image.filename, 'landscape');
      }
    })
  );
  
  return orientations;
};

/**
 * Calculate aspect ratio padding for CSS
 * 
 * @param {object} sizeConfig - Product configuration
 * @param {string} orientation - 'portrait' or 'landscape'
 * @returns {number} Padding percentage for aspect-ratio-based container
 */
export const calculateAspectPadding = (sizeConfig, orientation = 'landscape') => {
  if (!sizeConfig?.crop_ratio_x || !sizeConfig?.crop_ratio_y) {
    return 100; // Default square
  }

  const ratioX = sizeConfig.crop_ratio_x;
  const ratioY = sizeConfig.crop_ratio_y;
  const isNaturallyLandscape = ratioX > ratioY;

  if (orientation === 'portrait') {
    // Portrait: taller container
    return isNaturallyLandscape
      ? (ratioX / ratioY) * 100  // 6x4 portrait: 6/4 = 150%
      : (ratioY / ratioX) * 100; // 8x10 portrait: 10/8 = 125%
  } else {
    // Landscape: wider container
    return isNaturallyLandscape
      ? (ratioY / ratioX) * 100  // 6x4 landscape: 4/6 = 66.67%
      : (ratioX / ratioY) * 100; // 8x10 landscape: 8/10 = 80%
  }
};
